import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { buildSeo } from "@/lib/seo";
import { supabase } from "@/integrations/supabase/client";
import { getCheckoutSessionStatus } from "@/utils/payments.functions";
import { getStripeEnvironment } from "@/lib/stripe";
import { Loader2 } from "lucide-react";

export const Route = createFileRoute("/checkout/return")({
  head: () => buildSeo({ path: "/checkout/return", title: "Checkout complete", description: "Your Reality Twin subscription is being activated. We're confirming your payment with Stripe and unlocking your plan — this usually takes just a few seconds.", noindex: true }),
  validateSearch: (search: Record<string, unknown>): { session_id?: string } => ({
    session_id: typeof search.session_id === "string" ? search.session_id : undefined,
  }),
  errorComponent: ({ error }) => <div className="p-8 text-center text-sm text-red-700">Checkout error: {error.message}</div>,
  notFoundComponent: () => <div className="p-8 text-center">Not found</div>,
  component: CheckoutReturn,
});

type State = "verifying" | "activating" | "ready" | "failed";

function CheckoutReturn() {
  const { session_id } = Route.useSearch();
  const navigate = useNavigate();
  const sessionStatus = useServerFn(getCheckoutSessionStatus);
  const [state, setState] = useState<State>(session_id ? "verifying" : "failed");
  const [message, setMessage] = useState<string>("Confirming your payment…");

  useEffect(() => {
    if (!session_id) return;
    let cancelled = false;

    async function run() {
      try {
        // 1) Confirm the checkout session with Stripe.
        const status = await sessionStatus({ data: { sessionId: session_id!, environment: getStripeEnvironment() } });
        if (cancelled) return;
        if ("error" in status) { setState("failed"); setMessage(status.error); return; }
        if (status.status !== "complete") {
          setState("failed");
          setMessage("Payment wasn't completed. You can retry from the pricing page.");
          return;
        }

        // 2) Poll subscriptions table until the webhook lands (max ~10s).
        setState("activating");
        setMessage("Activating your subscription…");
        const { data: userRes } = await supabase.auth.getUser();
        const userId = userRes?.user?.id;
        const env = getStripeEnvironment();
        const started = Date.now();

        while (!cancelled && Date.now() - started < 12_000) {
          if (userId) {
            const { data: sub } = await supabase
              .from("subscriptions")
              .select("status, current_period_end")
              .eq("user_id", userId)
              .eq("environment", env)
              .order("created_at", { ascending: false })
              .limit(1)
              .maybeSingle();
            if (sub && ["active", "trialing"].includes(sub.status as string)) {
              if (cancelled) return;
              setState("ready");
              setMessage("You're all set — redirecting to your dashboard…");
              setTimeout(() => { if (!cancelled) navigate({ to: "/dashboard" }); }, 900);
              return;
            }
          }
          await new Promise(r => setTimeout(r, 800));
        }

        // Timed out — redirect anyway; webhook will catch up shortly.
        if (!cancelled) {
          setState("ready");
          setMessage("Almost done — taking you to your dashboard.");
          setTimeout(() => { if (!cancelled) navigate({ to: "/dashboard" }); }, 900);
        }
      } catch (e: any) {
        if (!cancelled) { setState("failed"); setMessage(e?.message ?? "Something went wrong."); }
      }
    }
    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session_id]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="max-w-md text-center">
        {state !== "failed" ? (
          <>
            <div className="flex justify-center mb-6"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
            <div className="font-serif text-3xl">{state === "ready" ? "Thank you 🎉" : "One moment…"}</div>
            <p className="mt-4 text-muted-foreground">{message}</p>
          </>
        ) : (
          <>
            <div className="font-serif text-3xl">Payment not confirmed</div>
            <p className="mt-4 text-muted-foreground">{message}</p>
            <Link to="/pricing" className="mt-6 inline-flex items-center rounded-full bg-foreground px-5 py-3 text-sm font-medium text-background">
              Back to pricing
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
